import { Button, Form, Input, message, } from "antd";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";


import Spinner from "../../../../components/layout/spinner/Spinner";
import ContentSection from "../../../../components/layout/mainContent/ContentSection";
import MainContentFooter from "../../../../components/layout/mainContent/MainContentFooter";
import { Colors } from "../../../../constants/Colors";

import { useAddGoGoLiveParentGuideLocaleMutation } from "../../../../graphql/operations/add-parent-guide-locale";
import { useUpdateGoGoLiveParentGuideLocaleMutation } from "../../../../graphql/operations/update-parent-guide-locale";
import { GoGoLiveParentGuideDetailInfo } from "../../../../graphql/operations/@types";

interface GoGoLiveDetailTabLocaleProps {
  englishInfo: GoGoLiveParentGuideDetailInfo | undefined;
  localeForm: any;
  market: string;
  canEdit: boolean;
  isView: boolean;
  editMode: number;
  userName: string | null | undefined;
}

const textStyle: React.CSSProperties = {
  color: Colors.notEditableText,
  lineHeight: "22px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  margin: 0,
  padding: '4px 0',
};

const GoGoLiveDetailTabLocale = ({
  englishInfo,
  localeForm,
  market,
  canEdit,
  isView,
  editMode, //editMode >> 0: add 1: update
  userName,
}: GoGoLiveDetailTabLocaleProps) => {
  const navigate = useNavigate();
  const settingsForm = localeForm;

  const [addParentGuideLocale, { loading: addLoading }] = useAddGoGoLiveParentGuideLocaleMutation();
  const [updateParentGuideLocale, { loading: updateLoading }] = useUpdateGoGoLiveParentGuideLocaleMutation();

  const handleSubmit = (values: any) => {
    const formData = {
      ...values,
      goGoLiveParentGuideId: englishInfo?.goGoLiveParentGuideId,
      market: market,
      learningGoals: values.learningGoals.filter((item: string) => item.trim() !== ""),
      user: userName,
    }
    if (editMode === 0) {
      addParentGuideLocale({
        variables: {
          ...formData,
        },
      }).then(() => {
        message.success(
          "Go, Go Live! details have been successfully submitted."
        );
        navigate("/club-app/ves");
      })
        .catch((e) => console.log(e));
    } else {
      updateParentGuideLocale({
        variables: {
          ...formData,
        },
      }).then(() => {
        message.success(
          "Go, Go Live! details have been successfully submitted."
        );
        navigate("/club-app/ves");
      })
        .catch((e) => console.log(e));
    }
  };

  if (addLoading || updateLoading) {
    return <Spinner />;
  }

  return (
    <ContentSection title="" showTitle={false}>
      <Form
        form={settingsForm}
        name="settings"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 12 }}
        onFinish={handleSubmit}
        initialValues={{
          learningGoals: [''],
        }}
      >
        <Form.Item label="Year & month">
          <p style={textStyle}>
            {englishInfo?.yearAndMonth ? dayjs(englishInfo?.yearAndMonth).format("YYYY-MMM") : ""}
          </p>
        </Form.Item>
        <Form.Item label="CAP level & session">
          <p style={textStyle}>
            {englishInfo?.levelId}
            {englishInfo?.levelId && ", "}
            {englishInfo?.sessions.map((s) => s.sessionName).join(', ')}
          </p>
        </Form.Item>
        <Form.Item label="Lesson title">
          <p style={textStyle}>
            {englishInfo?.lessonTitle}
          </p>
        </Form.Item>
        <Form.Item label="Introduction">
          <p style={textStyle}>
            {englishInfo?.introduction}
          </p>
        </Form.Item>
        <Form.Item label="Learning goals">
          <div>
            {englishInfo?.learningGoals.map((item, index) => (
              <p key={index} style={textStyle}>
                {item}
              </p>
            ))}
          </div>
        </Form.Item>
        <Form.Item label="Show and tell">
          <p style={textStyle}>
            {englishInfo?.showAndTell}
          </p>
        </Form.Item>
        <div
          className="parent-container"
          style={{ backgroundColor: Colors.lightWhite }}
        >
          <h5>Localized Content  <span style={{ color: "rgba(0,0,0,0.45)", fontSize: "14px" }}>Press Enter to start a new paragraph</span></h5>
          <Form.Item name="goGoLiveParentGuideLocaleId" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            label="Introduction"
            name="introduction"
          >
            <Input.TextArea
              disabled={!canEdit}
              placeholder="Autosize height based on content lines" />
          </Form.Item>
          <Form.Item label="Learning Goals" className="no-margin-bottom">
            <Form.List name="learningGoals">
              {(fields, { add, remove }) => (
                <>{fields.map(({ key, name, ...restField }) => (
                  <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 8, height: 32 }}>
                    <Form.Item
                      {...restField}
                      name={name}
                      style={{ width: "100%" }}>
                      <Input
                        disabled={!canEdit}
                        placeholder="Autosize height based on content lines" />
                    </Form.Item>
                    {canEdit && (
                      <MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'rgba(0, 0, 0, 0.45)', }} />
                    )}
                  </div>
                ))}
                  {canEdit && (
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() => add("")}
                        block
                        icon={<PlusOutlined />}
                      >
                        Add Row
                      </Button>
                    </Form.Item>
                  )}
                </>
              )}
            </Form.List>
          </Form.Item>
          <Form.Item
            label="Show and tell"
            name="showAndTell"
          >
            <Input.TextArea
              disabled={!canEdit}
              placeholder="Autosize height based on content lines" />
          </Form.Item>
        </div>

        {!isView && (
          <MainContentFooter
            isLoading={addLoading || updateLoading}
            disabled={!canEdit}
            backTo="/club-app/ves"
          />
        )}
      </Form>
    </ContentSection>
  );
};

export default GoGoLiveDetailTabLocale;
